import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const ScribeMindPrivacyPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="scribemind-privacy-page">
      <section className="py-16 md:py-24 bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto">
            <Link to="/apps" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Apps
            </Link>
            
            <div className="flex items-center gap-4 mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_app-collective/artifacts/gv10910j_Screenshot%202026-03-05%20at%207.43.04%E2%80%AFPM.png" 
                alt="ScribeMind" 
                className="w-16 h-16 rounded-2xl"
              />
              <div>
                <p className="caption mb-1">Privacy Policy</p>
                <h1 className="heading-2">ScribeMind</h1>
              </div>
            </div>
            
            <div className="prose prose-zinc max-w-none">
              <p className="body-text mb-2"><strong>Effective Date:</strong> February 15, 2026</p>
              <p className="body-text mb-8"><strong>Last Updated:</strong> February 15, 2026</p>

              <div className="space-y-8 body-text">
                <p>
                  ScribeMind ("we," "our," or "us") respects your privacy. This Privacy Policy explains how we collect, use, disclose, retain, and protect information when you use the ScribeMind mobile application and related services (collectively, the "App").
                </p>
                <p>By downloading, accessing, or using the App, you agree to this Privacy Policy.</p>

                <section>
                  <h2 className="heading-3 mb-4">1. Information We Collect</h2>
                  <p>We may collect the following categories of information:</p>
                  
                  <h4 className="font-semibold mt-4 mb-2">Account Information</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Name</li>
                    <li>Email address</li>
                    <li>Authentication data from sign-in providers (e.g., Apple, Google)</li>
                  </ul>

                  <h4 className="font-semibold mt-4 mb-2">User Content</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Audio recordings captured through the App</li>
                    <li>Transcriptions, summaries, notes, and related text generated from recordings</li>
                    <li>Titles, tags, or labels you provide for recordings or notes</li>
                  </ul>

                  <h4 className="font-semibold mt-4 mb-2">Subscription and Purchase Information</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Subscription status and plan tier (e.g., Free, Premium)</li>
                    <li>Receipt metadata from in-app purchases</li>
                    <li>We do not collect full payment card details; payments are handled by the platform (Apple/Google).</li>
                  </ul>

                  <h4 className="font-semibold mt-4 mb-2">Device and Usage Information</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>App usage events (e.g., features used, screens viewed)</li>
                    <li>Device information (e.g., OS version, app version)</li>
                    <li>Crash and performance diagnostics</li>
                  </ul>

                  <h4 className="font-semibold mt-4 mb-2">Notification Preferences</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Settings you choose for push notifications and reminders</li>
                  </ul>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">2. How We Use Your Information</h2>
                  <p>We use your information to:</p>
                  <ul className="list-disc pl-6 mt-4 space-y-1">
                    <li>Create, manage, and personalize your account</li>
                    <li>Record, store, and process audio recordings and generate transcriptions, summaries, and related content</li>
                    <li>Provide app functionality such as organizing notes and sending reminders</li>
                    <li>Improve app performance, reliability, and user experience</li>
                    <li>Detect, prevent, and address fraud, abuse, and security issues</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">3. AI Processing</h2>
                  <p>
                    Our App uses AI services to process audio and text to provide core features such as transcription, summarization, and note generation. Audio and text content may be securely sent to our backend servers and AI service providers to fulfill these features.
                  </p>
                  <p className="mt-4 font-medium">We do not sell your personal recordings or content.</p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">4. How We Share Information</h2>
                  <p>We may share information with:</p>
                  <ul className="list-disc pl-6 mt-4 space-y-1">
                    <li>Service providers that help us operate the App (e.g., cloud hosting, authentication, analytics, AI processing)</li>
                    <li>App platform providers (e.g., Apple, Google) to verify purchases</li>
                    <li>Legal authorities when required by law or to protect rights, safety, or property</li>
                    <li>Business transfers (e.g., merger or asset sale)</li>
                  </ul>
                  <p className="mt-4 font-medium">We do not sell your personal information.</p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">5. Data Retention</h2>
                  <p>
                    We retain your information as long as needed to provide the App and for legitimate business or legal purposes. If you stop using the App, some data may remain in backups or logs for a limited period as required for security, legal, or operational reasons.
                  </p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">6. Account Deletion</h2>
                  <p>
                    You may request deletion of your account and personal data by contacting us at{" "}
                    <a href="mailto:info@theappfam.com" className="text-zinc-900 underline underline-offset-4">info@theappfam.com</a>
                  </p>
                  <p className="mt-2">
                    When your account is deleted, we will delete or anonymize personal data we control, except where retention is required by law or for security, fraud prevention, billing, or dispute resolution.
                  </p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">7. Security</h2>
                  <p>
                    We use reasonable administrative, technical, and organizational safeguards to protect your information. However, no system is completely secure, and we cannot guarantee absolute protection.
                  </p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">8. Children's Privacy</h2>
                  <p>
                    We do not knowingly collect personal information from children under the age of 13. If you believe a child has provided personal data without parental consent, contact us at{" "}
                    <a href="mailto:info@theappfam.com" className="text-zinc-900 underline underline-offset-4">info@theappfam.com</a>
                  </p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">9. Your Rights and Choices</h2>
                  <p>
                    Depending on your location, you may have rights to access, correct, delete, or restrict the use of your personal information. To exercise these rights, contact us at{" "}
                    <a href="mailto:info@theappfam.com" className="text-zinc-900 underline underline-offset-4">info@theappfam.com</a>
                  </p>
                  <p className="mt-4">You may also:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Disable push notifications through your device settings</li>
                    <li>Manage subscriptions through your platform subscription settings</li>
                  </ul>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">10. International Data Transfers</h2>
                  <p>
                    Your information may be processed in countries other than your own. Where applicable, we take reasonable steps to ensure appropriate safeguards are in place for cross-border transfers.
                  </p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">11. Third-Party Services</h2>
                  <p>The App may use third-party services, which have their own privacy policies. These may include:</p>
                  <ul className="list-disc pl-6 mt-4 space-y-1">
                    <li>Authentication services (Apple, Google)</li>
                    <li>Cloud hosting providers</li>
                    <li>Analytics and crash reporting services</li>
                    <li>AI processing providers</li>
                  </ul>
                  <p className="mt-4">We encourage you to review the privacy policies of these providers.</p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">12. Changes to This Privacy Policy</h2>
                  <p>
                    We may update this Privacy Policy from time to time. When we do, we will update the "Last Updated" date and, where required by law, provide notice to users.
                  </p>
                </section>

                <section>
                  <h2 className="heading-3 mb-4">13. Contact Us</h2>
                  <p>If you have questions, concerns, or requests regarding this Privacy Policy, contact us at:</p>
                  <p className="mt-4">
                    <strong>Email:</strong>{" "}
                    <a href="mailto:info@theappfam.com" className="text-zinc-900 underline underline-offset-4">info@theappfam.com</a><br />
                    <strong>Company Name:</strong> ScribeMind
                  </p>
                </section>

                {/* Addendum Section */}
                <div className="border-t border-zinc-200 pt-12 mt-12">
                  <h2 className="heading-2 mb-6">Privacy Policy Addendum (AI Processing and Data Sharing)</h2>
                  <p className="body-text mb-2"><strong>Effective Date:</strong> March 2026</p>
                  <p className="body-text mb-8"><strong>Applies to:</strong> ScribeMind iOS and Android apps</p>

                  <section>
                    <h3 className="heading-3 mb-4">1. Data We Collect</h3>
                    <p>To provide ScribeMind features, we may collect and process:</p>
                    <ul className="list-disc pl-6 mt-4 space-y-1">
                      <li><strong>Account data:</strong> name, email address, profile details (school, location, level/faculty, optional phone number)</li>
                      <li><strong>Lecture data:</strong> audio recordings uploaded by users, generated transcripts, summaries, concept explanations, quiz/exam content</li>
                      <li><strong>Usage data:</strong> feature usage events, purchase/credit activity, app diagnostics and crash reports</li>
                      <li><strong>Device/app data:</strong> device identifiers, OS/app version, and technical logs necessary for security and service reliability</li>
                    </ul>
                  </section>

                  <section className="mt-8">
                    <h3 className="heading-3 mb-4">2. How Data Is Collected</h3>
                    <p>We collect data when you:</p>
                    <ul className="list-disc pl-6 mt-4 space-y-1">
                      <li>Create an account or update profile information</li>
                      <li>Record and upload lecture audio</li>
                      <li>Request AI-powered features (transcription, translation, summaries, concepts, quizzes, exams)</li>
                      <li>Make in-app purchases</li>
                      <li>Use the app (analytics, diagnostics, crash reporting)</li>
                    </ul>
                    <p className="mt-4">Data is collected directly from user input, uploaded files, and automated app/service logs.</p>
                  </section>

                  <section className="mt-8">
                    <h3 className="heading-3 mb-4">3. Third-Party AI Service Provider</h3>
                    <p>ScribeMind uses <strong>OpenAI, LLC</strong> to provide AI features.</p>
                    <p className="mt-2">When you use AI features, we may send relevant data to OpenAI, including:</p>
                    <ul className="list-disc pl-6 mt-4 space-y-1">
                      <li>Audio/transcript text</li>
                      <li>User prompts/questions</li>
                      <li>Context needed to generate requested outputs</li>
                    </ul>
                  </section>

                  <section className="mt-8">
                    <h3 className="heading-3 mb-4">4. Purpose of Sharing with OpenAI</h3>
                    <p>Data is shared with OpenAI only to perform user-requested AI functionality, including:</p>
                    <ul className="list-disc pl-6 mt-4 space-y-1">
                      <li>Speech-to-text transcription</li>
                      <li>Text translation</li>
                      <li>Lecture summary and concept generation</li>
                      <li>Quiz and exam generation</li>
                    </ul>
                    <p className="mt-4 font-medium">We do not share data with OpenAI for unrelated marketing purposes.</p>
                  </section>

                  <section className="mt-8">
                    <h3 className="heading-3 mb-4">5. Protections and Safeguards</h3>
                    <p>We apply the following protections:</p>
                    <ul className="list-disc pl-6 mt-4 space-y-1">
                      <li>Data transmission over encrypted channels (TLS/HTTPS)</li>
                      <li>Access controls and authentication for backend resources</li>
                      <li>Principle-of-least-privilege for service operations</li>
                      <li>Logging/monitoring to detect abuse and service issues</li>
                      <li>Data minimization: only data needed for requested feature processing is sent</li>
                    </ul>
                  </section>

                  <section className="mt-8">
                    <h3 className="heading-3 mb-4">6. User Consent</h3>
                    <p>
                      Before lecture processing, users are informed that data is sent to OpenAI, LLC for AI processing and must provide consent in-app before proceeding.
                    </p>
                  </section>

                  <section className="mt-8">
                    <h3 className="heading-3 mb-4">7. Retention</h3>
                    <p>
                      We retain personal data only as long as necessary to provide services, comply with legal obligations, resolve disputes, and enforce agreements. Users may request account/data deletion in-app where available.
                    </p>
                  </section>

                  <section className="mt-8">
                    <h3 className="heading-3 mb-4">8. Your Choices</h3>
                    <p>You may:</p>
                    <ul className="list-disc pl-6 mt-4 space-y-1">
                      <li>Choose not to use AI-powered features</li>
                      <li>Decline optional profile fields</li>
                      <li>Request deletion of your account and associated data (where provided in-app)</li>
                    </ul>
                  </section>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ScribeMindPrivacyPage;
